import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/common/StatusBadge';
import {
  Laptop, Calendar, Cpu, Package, User, RefreshCw, Shield,
  Clock, Mail, AlertTriangle, Send, Sparkles, LifeBuoy, Flag, Check, Loader, ArrowLeft,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// NOTE: this must be a real email address — it's used in mailto: links below.
const IT_EMAIL = process.env.REACT_APP_SYSADMIN_EMAIL || 'sysadmin@mindteck.com';
// Separate helpdesk portal link (not an email) — shown as an alternative
// support channel alongside the mailto quick actions.
const HELPDESK_URL = 'https://helpdesk.mindteck.com/open.php';

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.max(0, Math.floor(diff / 86400000));
};

const ISSUE_FIELDS = [
  'Configuration', 'Processor', 'RAM', 'Storage', 'Serial No',
  'Brand / Model', 'Allocation Date', 'Department', 'Project', 'Client',
  'My Name / Employee ID', 'Other',
];

// ── Report Wrong Details Page ─────────────────────────────────────────────────
// Inline page (not a popup) — matches the app's existing detail-page pattern.
// Lets an employee flag incorrect/missing data on their own laptop or
// allocation record. On submit, the backend emails sysadmin@mindteck.com
// with the employee + asset context plus what the employee says is wrong.
function ReportWrongDetailsPage({ allocation, user, onBack }) {
  const [field,       setField]       = useState(ISSUE_FIELDS[0]);
  const [description, setDescription] = useState('');
  const [status,      setStatus]      = useState('idle'); // idle | sending | done | error
  const [errorMsg,    setErrorMsg]    = useState('');

  const handleSubmit = async () => {
    if (!description.trim()) {
      setErrorMsg('Please describe what is wrong before submitting.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/allocations/${allocation.id || allocation.allocation_id}/report-detail-issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          field,
          description: description.trim(),
          asset_id: allocation.asset_id,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to send report');
      setStatus('done');
    } catch (e) {
      setErrorMsg(e.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="fade-in">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 24, paddingBottom: 16,
        borderBottom: '1px solid var(--border)',
      }}>
        <button className="btn btn-secondary" onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={15} /> Back to Dashboard
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Flag size={18} color="#f59e0b" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Report Wrong Details</h2>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 48, marginBottom: 24 }}>
        Spotted incorrect or missing info on your laptop or allocation record? Let IT know and they'll correct it.
      </p>

      {status === 'done' ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <Check size={26} color="#22c55e" />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Report sent</h3>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 22, maxWidth: 360, margin: '0 auto 22px' }}>
            IT has been notified and will review the details you flagged.
          </p>
          <button className="btn btn-primary" onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
        </div>
      ) : (
        <div className="card" style={{ maxWidth: 560 }}>
          {/* Read-only context, so IT knows exactly whose record this is */}
          <div style={{
            padding: '12px 14px', borderRadius: 8, background: 'var(--surface2)',
            border: '1px solid var(--border)', marginBottom: 20, fontSize: 13,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: 'var(--text-muted)' }}>Employee</span>
              <span style={{ fontWeight: 600 }}>{user?.name} ({user?.emp_id || user?.empId || '—'})</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>Asset ID</span>
              <span style={{ fontWeight: 600, fontFamily: 'var(--mono)' }}>{allocation.asset_id}</span>
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Which field is wrong?
          </label>
          <select
            className="form-input"
            value={field}
            onChange={e => setField(e.target.value)}
            style={{ width: '100%', marginBottom: 18 }}
          >
            {ISSUE_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            What's wrong / what should it be?
          </label>
          <textarea
            className="form-input"
            rows={5}
            placeholder="e.g. Processor should be i5 11th Gen, RAM should be 16GB — currently showing blank"
            value={description}
            onChange={e => setDescription(e.target.value)}
            style={{ width: '100%', resize: 'vertical', marginBottom: 10 }}
          />

          {errorMsg && (
            <div style={{ fontSize: 12.5, color: '#ef4444', marginBottom: 14 }}>{errorMsg}</div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-secondary" onClick={onBack} disabled={status === 'sending'}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowLeft size={14} /> Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={status === 'sending'}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              {status === 'sending' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
              {status === 'sending' ? 'Sending…' : 'Send to IT'}
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  // ── Now an ARRAY — an employee can have more than one active laptop ──
  const [allocations, setAllocations] = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState('');
  const [reportPageAlloc, setReportPageAlloc] = useState(null); // which allocation's report page is open

  const fetchMyLaptops = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API}/allocations/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Backend may still return a single object on older deployments —
        // normalize to an array either way so this never breaks.
        const list = Array.isArray(data.data) ? data.data : (data.data ? [data.data] : []);
        setAllocations(list);
      } else {
        setError(data.message || 'Failed to load data');
      }
    } catch (err) {
      setError('Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMyLaptops(); }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'50vh' }}>
      <div style={{ color:'var(--text-muted)', fontSize:14 }}>Loading your dashboard…</div>
    </div>
  );

  // ── Render: Report Wrong Details inner page ──
  if (reportPageAlloc) {
    return (
      <ReportWrongDetailsPage
        allocation={reportPageAlloc}
        user={user}
        onBack={() => setReportPageAlloc(null)}
      />
    );
  }

  const hasLaptops = allocations.length > 0;

  const mailtoIssueFor = (allocation) => `mailto:${IT_EMAIL}?subject=${encodeURIComponent(
    `Laptop Issue — ${allocation?.asset_id || 'N/A'} (${user?.name || ''})`
  )}&body=${encodeURIComponent(
    `Hi IT Team,\n\nI'm having an issue with my allocated laptop.\n\nAsset ID: ${allocation?.asset_id || 'N/A'}\nEmployee: ${user?.name || ''} (${user?.email || ''})\n\nIssue description:\n`
  )}`;

  const mailtoAccessoryFor = (allocation) => `mailto:${IT_EMAIL}?subject=${encodeURIComponent(
    `Accessory Request — ${user?.name || ''}`
  )}&body=${encodeURIComponent(
    `Hi IT Team,\n\nI'd like to request the following accessory for my allocated laptop (${allocation?.asset_id || 'N/A'}):\n\n`
  )}`;

  return (
    <div className="fade-in">
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(79,142,247,0.12), rgba(124,106,247,0.08))',
        border: '1px solid rgba(79,142,247,0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px 28px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 800, color: '#fff', flexShrink: 0,
          boxShadow: '0 6px 18px rgba(79,142,247,0.35)',
        }}>
          {user?.name?.[0]?.toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.02em' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <div style={{ color: 'var(--text-muted)', fontSize: 13.5 }}>
            {user?.email} &nbsp;·&nbsp;
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Employee Portal</span>
            {hasLaptops && (
              <>
                &nbsp;·&nbsp;
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                  {allocations.length} laptop{allocations.length === 1 ? '' : 's'} allocated
                </span>
              </>
            )}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={fetchMyLaptops}>
          <RefreshCw size={13}/> Refresh
        </button>
      </div>

      {error && (
        <div style={{ padding:'14px 18px', background:'var(--red-bg)', border:'1px solid rgba(248,113,113,.2)', borderRadius:'var(--radius)', color:'var(--red)', marginBottom:20, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
          <AlertTriangle size={15} />
          {error}
        </div>
      )}

      {!hasLaptops ? (
        /* No active allocation */
        <div className="card" style={{ textAlign:'center', padding:'60px 40px' }}>
          <div style={{ width:64, height:64, borderRadius:16, background:'var(--surface2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
            <Package size={28} color="var(--text-muted)"/>
          </div>
          <h2 style={{ fontSize:18, fontWeight:700, marginBottom:10 }}>No Laptop Allocated</h2>
          <p style={{ color:'var(--text-muted)', fontSize:14, maxWidth:380, margin:'0 auto 20px' }}>
            You don't have an active laptop allocation. Please contact your IT team.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <a
              href={`mailto:${IT_EMAIL}?subject=${encodeURIComponent('No Laptop Allocated — ' + (user?.name || ''))}`}
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'rgba(79,142,247,0.08)', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'var(--radius)', fontSize:13, color:'var(--accent)', textDecoration:'none' }}
            >
              <Shield size={14}/>
              IT Support: <strong>{IT_EMAIL}</strong>
            </a>
            <a
              href={HELPDESK_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'rgba(79,142,247,0.08)', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'var(--radius)', fontSize:13, color:'var(--accent)', textDecoration:'none' }}
            >
              <LifeBuoy size={14}/>
              Mindteck IT Support
            </a>
          </div>
        </div>
      ) : (
        <>
          {/* One block per laptop */}
          {allocations.map((allocation, idx) => {
            const daysAllocated = daysSince(allocation?.allocation_date);
            return (
              <div key={allocation.asset_id || idx} style={{ marginBottom: 24 }}>
                {allocations.length > 1 && (
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10,
                  }}>
                    Laptop {idx + 1} of {allocations.length}
                  </div>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

                  {/* Laptop Card */}
                  <div className="card">
                    <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
                      <div style={{ width:44, height:44, borderRadius:12, background:'var(--accent-glow)', border:'1px solid var(--accent)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Laptop size={22} color="var(--accent)"/>
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontFamily:'var(--mono)', fontSize:16, fontWeight:800, color:'var(--accent)' }}>{allocation.asset_id}</div>
                        <div style={{ fontSize:12.5, color:'var(--text-muted)' }}>{allocation.brand} {allocation.model}</div>
                      </div>
                      <StatusBadge status="Active"/>
                    </div>

                    <div className="section-title">
                      <Cpu size={12} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }}/>
                      Specifications
                    </div>
                    <div className="info-row"><span className="info-label">Configuration</span><span className="info-value">{allocation.config||'—'}</span></div>
                    <div className="info-row"><span className="info-label">Processor</span><span className="info-value">{allocation.processor||'—'}</span></div>
                    <div className="info-row"><span className="info-label">RAM</span><span className="info-value">{allocation.ram||'—'}</span></div>
                    <div className="info-row"><span className="info-label">Storage</span><span className="info-value">{allocation.storage||'—'}</span></div>
                    <div className="info-row"><span className="info-label">Serial No</span><span className="info-value" style={{ fontFamily:'var(--mono)', fontSize:12 }}>{allocation.serial||'—'}</span></div>

                    <button
                      onClick={() => setReportPageAlloc(allocation)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, marginTop: 14,
                        padding: '8px 12px', width: '100%', justifyContent: 'center',
                        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
                        borderRadius: 'var(--radius)', color: '#b45309', fontSize: 12.5, fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Flag size={13} /> Report Wrong Details
                    </button>
                  </div>

                  {/* Allocation + Timeline */}
                  <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                    <div className="card">
                      <div className="section-title">
                        <Calendar size={12} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }}/>
                        Allocation Details
                      </div>
                      <div className="info-row"><span className="info-label">Allocated On</span><span className="info-value" style={{ fontFamily:'var(--mono)', fontSize:12 }}>{allocation.allocation_date?.split('T')[0]}</span></div>
                      <div className="info-row"><span className="info-label">Department</span><span className="info-value">{allocation.department||'—'}</span></div>
                      <div className="info-row"><span className="info-label">Project</span><span className="info-value">{allocation.project||'—'}</span></div>
                      <div className="info-row"><span className="info-label">Client</span><span className="info-value">{allocation.client||'—'}</span></div>
                      <div className="info-row"><span className="info-label">Accessories</span><span className="info-value">{(allocation.accessories||[]).join(', ')||'None'}</span></div>
                    </div>

                    {/* Device Timeline */}
                    <div className="card" style={{ background:'rgba(52,211,153,0.04)', border:'1px solid rgba(52,211,153,0.18)' }}>
                      <div className="section-title">
                        <Clock size={12} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }}/>
                        Device Timeline
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'6px 0 4px' }}>
                        <div style={{
                          width:52, height:52, borderRadius:'50%',
                          background:'rgba(52,211,153,0.12)', border:'2px solid var(--green)',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:16, fontWeight:800, color:'var(--green)', flexShrink:0,
                        }}>
                          {daysAllocated ?? '—'}
                        </div>
                        <div>
                          <div style={{ fontSize:13.5, fontWeight:700 }}>
                            {daysAllocated != null ? `${daysAllocated} day${daysAllocated === 1 ? '' : 's'} with you` : 'Allocation date unavailable'}
                          </div>
                          <div style={{ fontSize:12, color:'var(--text-muted)' }}>
                            Since {allocation.allocation_date?.split('T')[0] || '—'}
                          </div>
                        </div>
                      </div>
  x                  </div>
                  </div>

                  {/* Quick Actions — per laptop, since issue reports need to reference a specific asset */}
                  <div className="card" style={{ gridColumn:'span 2', background:'rgba(79,142,247,0.04)', border:'1px solid rgba(79,142,247,0.15)' }}>
                    <div className="section-title">
                      <Sparkles size={12} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }}/>
                      Quick Actions — {allocation.asset_id}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginTop:10 }}>
                      <div>
                        <a href={mailtoIssueFor(allocation)} style={quickActionStyle}>
                          <div style={quickActionIconStyle('rgba(248,113,113,0.14)', 'var(--red)')}>
                            <AlertTriangle size={17}/>
                          </div>
                          <div>
                            <div style={{ fontSize:13.5, fontWeight:700, marginBottom:2 }}>Report an Issue</div>
                            <div style={{ fontSize:12, color:'var(--text-muted)' }}>Hardware or software problem with this laptop</div>
                          </div>
                          <Send size={13} style={{ marginLeft:'auto', color:'var(--text-muted)' }}/>
                        </a>
                        <a
                          href={HELPDESK_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={helpdeskLinkStyle}
                        >
                          <LifeBuoy size={12}/> Mindteck IT Support
                        </a>
                      </div>

                      <div>
                        <a href={mailtoAccessoryFor(allocation)} style={quickActionStyle}>
                          <div style={quickActionIconStyle('rgba(79,142,247,0.14)', 'var(--accent)')}>
                            <Package size={17}/>
                          </div>
                          <div>
                            <div style={{ fontSize:13.5, fontWeight:700, marginBottom:2 }}>Request an Accessory</div>
                            <div style={{ fontSize:12, color:'var(--text-muted)' }}>Mouse, bag, adapter, or other equipment</div>
                          </div>
                          <Send size={13} style={{ marginLeft:'auto', color:'var(--text-muted)' }}/>
                        </a>
                        <a
                          href={HELPDESK_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={helpdeskLinkStyle}
                        >
                          <LifeBuoy size={12}/> Mindteck IT Support
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, paddingTop:14, borderTop:'1px solid rgba(255,255,255,0.06)' }}>
            <User size={15} color="var(--accent)" style={{ flexShrink:0 }}/>
            <div style={{ fontSize:13, color:'var(--text-muted)' }}>
              For swaps and returns, only IT staff can process these — reach out to{' '}
              <a href={`mailto:${IT_EMAIL}`} style={{ color:'var(--accent)', textDecoration:'none', fontWeight:600 }}>
                <Mail size={11} style={{ display:'inline', marginRight:3, verticalAlign:'middle' }}/>{IT_EMAIL}
              </a>
              {' '}or the{' '}
              <a href={HELPDESK_URL} target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)', textDecoration:'none', fontWeight:600 }}>
                Mindteck IT Support portal
              </a>.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const quickActionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 16px',
  background: 'var(--surface2)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 'var(--radius)',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'transform 0.15s, border-color 0.15s',
};

const quickActionIconStyle = (bg, color) => ({
  width: 36, height: 36, borderRadius: 10,
  background: bg, color,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
});

// Small secondary link shown under each Quick Action card, pointing to the
// Mindteck IT Support helpdesk portal as an alternative to email.
const helpdeskLinkStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 8,
  padding: '6px 4px',
  fontSize: 11.5,
  color: 'var(--accent)',
  textDecoration: 'none',
  fontWeight: 600,
};
